<template>
    <div class="table-controls">
        <TableToolbar
            v-if="showToolbar"
            ref="toolbar"
            :search-value="searchValue"
            :search-placeholder="resolvedSearchPlaceholder"
            :has-search="hasSearchFilter"
            :has-filters="hasNonSearchFilters && !forceFiltersPanelOpen"
            :has-active-filters="hasActiveNonSearchFilters"
            :filters-panel-open="effectiveFiltersPanelOpen"
            :views-scope="toolbarViewsScope"
            :views-exclude-keys="viewsExcludeKeys"
            :filters="filters"
            :views-menu-open="viewsMenuOpen"
            :has-active-view="!!activeViewLabel"
            :columns-menu-open="columnsMenuOpen"
            :enable-column-management="enableColumnManagement"
            :columns="columns"
            :column-state="columnState"
            :columns-customized="columnsCustomized"
            :search-product-completion="searchProductCompletion"
            :product-search-params="productSearchParams"
            :exclude-product-ids="excludeProductIds"
            :autofocus="shouldAutofocusSearch"
            @search-change="onSearchChange"
            @product-select="onProductSelect"
            @toggle-filters="toggleFiltersPanel"
            @toggle-views="viewsMenuOpen = !viewsMenuOpen"
            @toggle-columns="columnsMenuOpen = !columnsMenuOpen"
            @view-apply="onViewApply"
            @view-reset="onViewReset"
            @active-view-change="onActiveViewChange"
            @columns-change="onColumnsChange"
            @columns-reset="onColumnsReset"
        />

        <TableViewsBar
            v-if="showViewsBar && viewsScope && hasAnyActiveFilters"
            ref="viewsBar"
            :views-scope="viewsScope"
            :filters="filters"
            :views-exclude-keys="viewsExcludeKeys"
            :show-filter-actions="effectiveFiltersPanelOpen"
            @apply="onViewApply"
            @reset="onViewReset"
            @active-view-change="onActiveViewChange"
        />

        <TableFiltersBar
            v-if="hasNonSearchFilters && showFiltersBar"
            :filters="filters"
            :full-filters-conf="fullFiltersConf"
            :panel-open="effectiveFiltersPanelOpen"
            :active-view-label="activeViewLabel"
            :quick-filter-count="quickFilterCount"
            @filter-change="onFilterPillChange"
            @reset="resetFilters"
            @clear-view="onViewReset"
            @remove-product="onRemoveProduct"
        />

        <Filters
            ref="filtersEngine"
            headless
            :filters-conf="filtersConf"
            :filters="filters"
            :enable-ordering="enableOrdering"
            :exclude-from-ordering="excludeFromOrdering"
            @on-filters-change="$emit('on-filters-change', $event)"
            @conf-updated="fullFiltersConf = $event"
        />
    </div>
</template>

<script>
import Filters from "@/components/filters/filters";
import TableToolbar from "./table-toolbar";
import TableFiltersBar from "./table-filters-bar";
import TableViewsBar from "./table-views-bar";
import {
    buildFiltersConfSignature,
    countActiveFilters,
    findActiveFilterKeysMissingFromConf,
} from "@/utils/tableFilterLabels";
import {
    applyColumnState,
    buildDefaultColumnState,
    columnStorageKeyForLayout,
    enforceColumnConstraints,
    isColumnStateCustomized,
    loadColumnState,
    saveColumnState,
} from "@/utils/tableColumnState";
import {
    loadFiltersPanelOpen,
    saveFiltersPanelOpen,
} from "@/utils/tableFiltersPanelState";

export default {
    name: "TableControls",
    components: { Filters, TableToolbar, TableFiltersBar, TableViewsBar },
    props: {
        filtersConf: {
            type: Array,
            default: () => [],
        },
        filters: {
            type: Object,
            required: true,
        },
        viewsScope: {
            type: String,
            default: null,
        },
        viewsExcludeKeys: {
            type: Array,
            default: () => [],
        },
        enableOrdering: {
            type: Boolean,
            default: false,
        },
        excludeFromOrdering: {
            type: Array,
            default: () => [],
        },
        columns: {
            type: Array,
            default: () => [],
        },
        enableColumnManagement: {
            type: Boolean,
            default: true,
        },
        columnsScope: {
            type: String,
            default: null,
        },
        quickFilterCount: {
            type: Number,
            default: 3,
        },
        searchPlaceholder: {
            type: String,
            default: null,
        },
        showViewsBar: {
            type: Boolean,
            default: false,
        },
        searchProductCompletion: {
            type: Boolean,
            default: false,
        },
        forceFiltersPanelOpen: {
            type: Boolean,
            default: false,
        },
        autofocusSearch: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            filtersPanelOpen: false,
            viewsMenuOpen: false,
            columnsMenuOpen: false,
            activeViewLabel: null,
            columnState: buildDefaultColumnState(this.columns, this.layoutBreakpointName()),
            fullFiltersConf: [],
            filtersConfSignature: "",
            syncingFiltersConf: false,
            pendingMissingFilterKeysKey: "",
        };
    },
    computed: {
        hasSearchFilter() {
            if (this.searchProductCompletion) {
                return true;
            }
            return this.fullFiltersConf.some((c) => c.type === "search");
        },
        hasNonSearchFilters() {
            return this.fullFiltersConf.some((c) => c.type !== "search");
        },
        hasActiveNonSearchFilters() {
            return countActiveFilters(this.fullFiltersConf, this.filters) > 0;
        },
        hasAnyActiveFilters() {
            if (this.activeViewLabel) {
                return true;
            }
            return (
                countActiveFilters(this.fullFiltersConf, this.filters, {
                    excludeSearch: false,
                }) > 0
            );
        },
        toolbarViewsScope() {
            // Icon in the toolbar whenever views are enabled; the under-search bar
            // is an alternate layout (showViewsBar) that hides the icon.
            if (this.showViewsBar) {
                return null;
            }
            return this.viewsScope;
        },
        showToolbar() {
            return (
                this.hasSearchFilter ||
                this.hasNonSearchFilters ||
                this.searchProductCompletion ||
                !!this.toolbarViewsScope ||
                (this.enableColumnManagement && this.columns.length)
            );
        },
        effectiveFiltersPanelOpen() {
            return this.forceFiltersPanelOpen || this.filtersPanelOpen;
        },
        showFiltersBar() {
            if (this.forceFiltersPanelOpen && this.hasNonSearchFilters) {
                return true;
            }
            if (this.hasActiveNonSearchFilters) {
                return true;
            }
            return this.filtersPanelOpen;
        },
        searchValue() {
            const searchConf = this.fullFiltersConf.find((c) => c.type === "search");
            const field = searchConf?.field || "search";
            return this.filters?.[field] || "";
        },
        resolvedSearchPlaceholder() {
            if (this.searchPlaceholder) {
                return this.searchPlaceholder;
            }
            const searchConf = this.fullFiltersConf.find((c) => c.type === "search");
            return searchConf?.placeholder || searchConf?.label || this.$translate("search");
        },
        layoutBreakpoint() {
            return this.layoutBreakpointName();
        },
        columnsCustomized() {
            return isColumnStateCustomized(
                this.columnState,
                this.columns,
                this.layoutBreakpoint,
            );
        },
        columnStorageKeyResolved() {
            return columnStorageKeyForLayout(this.columnsScope, this.layoutBreakpoint);
        },
        columnScopeForStorage() {
            return this.columnsScope;
        },
        filtersPanelStorageScope() {
            return this.columnsScope || this.viewsScope;
        },
        productSearchParams() {
            const params = { stock_tracking: true };
            if (this.filters?.stock) {
                params.stock = this.filters.stock;
            }
            if (this.filters?.producttype) {
                params.producttype = this.filters.producttype;
            }
            Object.keys(this.filters || {}).forEach((key) => {
                if (key.startsWith("metadata__") && this.filters[key]) {
                    params[key] = this.filters[key];
                }
            });
            return params;
        },
        excludeProductIds() {
            return (this.filters?.products || []).map((product) => product.id);
        },
        shouldAutofocusSearch() {
            if (this.autofocusSearch) {
                return true;
            }
            const expandedSearch = this.fullFiltersConf.find(
                (conf) => conf.type === "search"
            );
            if (expandedSearch && expandedSearch.focus) {
                return true;
            }
            return (this.filtersConf || []).some((item) => {
                if (!item || typeof item !== "object") {
                    return false;
                }
                const isSearch =
                    item.preset === "search" || item.type === "search";
                return isSearch && !!item.focus;
            });
        },
    },
    watch: {
        columns: {
            handler(columns) {
                this.initColumnState(columns);
            },
            immediate: true,
        },
        layoutBreakpoint() {
            this.initColumnState(this.columns);
        },
        filtersPanelStorageScope(scope, previousScope) {
            if (scope && scope !== previousScope) {
                this.restoreFiltersPanelOpen();
            }
        },
        hasAnyActiveFilters(active) {
            if (!active) {
                this.viewsMenuOpen = false;
            }
        },
        filters: {
            handler() {
                this.pendingMissingFilterKeysKey = "";
                this.syncFullFiltersConfIfNeeded();
            },
            deep: true,
        },
        filtersConf: {
            handler(newConf) {
                const signature = buildFiltersConfSignature(newConf);
                if (signature === this.filtersConfSignature) {
                    return;
                }
                this.filtersConfSignature = signature;
                this.pendingMissingFilterKeysKey = "";
                this.$nextTick(() => {
                    this.syncFullFiltersConfIfNeeded(true);
                });
            },
            deep: true,
            immediate: true,
        },
    },
    created() {
        if (this.forceFiltersPanelOpen) {
            this.filtersPanelOpen = true;
            return;
        }
        this.restoreFiltersPanelOpen();
    },
    mounted() {
        this.$nextTick(() => {
            this.emitColumnState();
            this.syncFullFiltersConfIfNeeded(true);
        });
    },
    methods: {
        syncFullFiltersConfIfNeeded(forceRefresh = false) {
            const engine = this.$refs.filtersEngine;
            if (!engine || this.syncingFiltersConf) {
                return;
            }

            if (!forceRefresh) {
                const missingKeys = findActiveFilterKeysMissingFromConf(
                    this.fullFiltersConf,
                    this.filters,
                );
                if (!missingKeys.length) {
                    this.pendingMissingFilterKeysKey = "";
                    return;
                }
                const missingKey = missingKeys.slice().sort().join(",");
                if (missingKey === this.pendingMissingFilterKeysKey) {
                    return;
                }
                this.pendingMissingFilterKeysKey = missingKey;
            }

            this.syncingFiltersConf = true;
            this.$nextTick(async () => {
                try {
                    await engine.refreshFiltersConf();
                } finally {
                    this.syncingFiltersConf = false;
                }
            });
        },
        restoreFiltersPanelOpen() {
            this.filtersPanelOpen = loadFiltersPanelOpen(this.filtersPanelStorageScope);
        },
        toggleFiltersPanel() {
            if (this.forceFiltersPanelOpen) {
                return;
            }
            this.filtersPanelOpen = !this.filtersPanelOpen;
            saveFiltersPanelOpen(this.filtersPanelStorageScope, this.filtersPanelOpen);
        },
        layoutBreakpointName() {
            // Without the column menu, `hide` stays a hard cutoff in the table.
            if (!this.enableColumnManagement) {
                return null;
            }
            return this.$vuetify?.breakpoint?.name || null;
        },
        initColumnState(columns) {
            const key = this.columnStorageKeyResolved;
            const scope = this.columnScopeForStorage;
            const breakpoint = this.layoutBreakpoint;
            this.columnState = loadColumnState(key, columns, scope, breakpoint);
            this.emitColumnState();
        },
        onSearchChange(value) {
            const searchConf = this.fullFiltersConf.find((c) => c.type === "search");
            if (!searchConf) {
                return;
            }
            this.onFilterPillChange({ filter: searchConf, value });
        },
        onFilterPillChange({ filter, value }) {
            const engine = this.$refs.filtersEngine;
            if (engine) {
                engine.filterChanged(filter, value);
            }
        },
        onViewApply(viewFilters) {
            this.$emit("view-apply", viewFilters);
        },
        onProductSelect(product) {
            const products = [...(this.filters.products || [])];
            if (products.some((entry) => String(entry.id) === String(product.id))) {
                return;
            }
            products.push(product);
            this.$emit("on-filters-change", {
                ...this.filters,
                products,
            });
        },
        onRemoveProduct(productId) {
            const products = (this.filters.products || []).filter(
                (entry) => String(entry.id) !== String(productId),
            );
            const next = { ...this.filters };
            if (products.length) {
                next.products = products;
            } else {
                delete next.products;
            }
            this.$emit("on-filters-change", next);
        },
        resetFilters() {
            const engine = this.$refs.filtersEngine;
            if (engine) {
                engine.resetFilters(["products"]);
            } else {
                const next = { ...this.filters };
                delete next.products;
                this.$emit("on-filters-change", next);
            }
            this.clearActiveViewState();
        },
        onViewReset() {
            this.resetFilters();
            this.$emit("view-reset");
        },
        clearActiveViewState() {
            this.activeViewLabel = null;
            this.$refs.toolbar?.$refs?.viewsMenu?.clearActiveView?.();
            this.$refs.viewsBar?.clearActiveView?.();
        },
        onActiveViewChange({ name }) {
            this.activeViewLabel = name || null;
        },
        onColumnsChange(nextState) {
            this.columnState = enforceColumnConstraints(nextState, this.columns);
            saveColumnState(this.columnStorageKeyResolved, this.columnState);
            this.emitColumnState();
        },
        onColumnsReset() {
            this.columnState = buildDefaultColumnState(this.columns, this.layoutBreakpoint);
            saveColumnState(this.columnStorageKeyResolved, this.columnState);
            this.emitColumnState();
        },
        emitColumnState() {
            const orderedColumns = applyColumnState(this.columns, this.columnState);
            this.$emit("columns-state-change", {
                columnState: this.columnState,
                columns: orderedColumns,
            });
        },
        /** Exposed for parent placeholder reset */
        resetAll() {
            this.resetFilters();
        },
    },
    i18n: {
        messages: {
            en: { search: "Search…" },
            fr: { search: "Rechercher…" },
        },
    },
};
</script>

<style lang="scss" scoped>
.table-controls {
    width: 100%;
}
</style>
